/**
 * useCommunicationBadge — total count of unread SMS + missed inbound calls.
 *
 * Drives the red badge on the Communication nav item in AdminLayout. Polls
 * both endpoints on a slow interval and re-runs on mount / window focus so
 * the count stays fresh without taking on a websocket dependency just for
 * a nav-bar dot.
 *
 * "Missed call" definition: an `incoming` direction call whose Twilio status
 * is one of (no-answer, busy, failed, canceled) — matching the set the
 * webhook already classifies as missed in voice.py. Count is filtered to
 * calls newer than `localStorage["calls.last_seen_at"]`, which is set when
 * the user opens /manage/call-history (so visiting the page clears the
 * missed-call portion of the badge without needing a server endpoint).
 *
 * "Unread SMS" is the sum over `/sms/unread-counts` (already keyed on
 * conversation phone). Visiting a conversation marks it read server-side
 * via the existing flow, so that side of the badge self-clears.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/shared/api';
import { useSmsWebSocket } from './useSmsWebSocket';

const POLL_INTERVAL_MS = 30_000;

export const CALL_HISTORY_LAST_SEEN_KEY = 'calls.last_seen_at';

/** Mark "all missed calls up to now" as seen. Call this when the user opens
 *  /manage/call-history so the badge's call portion clears. */
export function markCallHistorySeen(): void {
    try {
        localStorage.setItem(CALL_HISTORY_LAST_SEEN_KEY, new Date().toISOString());
    } catch {
        /* private mode / quota — non-fatal */
    }
}

function readCallsLastSeen(): number {
    try {
        const raw = localStorage.getItem(CALL_HISTORY_LAST_SEEN_KEY);
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t : 0;
    } catch {
        return 0;
    }
}

type Call = {
    direction?: string;
    status?: string;
    date_created?: string;
    start_time?: string;
    [k: string]: unknown;
};

const MISSED_STATUSES = new Set(['no-answer', 'busy', 'failed', 'canceled']);

function countMissedCalls(calls: Call[], sinceMs: number): number {
    return calls.reduce((acc, c) => {
        if (c.direction !== 'incoming') return acc;
        if (!c.status || !MISSED_STATUSES.has(c.status)) return acc;
        const tsStr = c.date_created || c.start_time;
        if (!tsStr) return acc + 1; // No timestamp → count it (better than dropping).
        const t = new Date(tsStr).getTime();
        if (!Number.isFinite(t)) return acc + 1;
        return t > sinceMs ? acc + 1 : acc;
    }, 0);
}

export interface CommunicationBadgeCounts {
    unreadSms: number;
    missedCalls: number;
    total: number;
}

export function useCommunicationBadge(): CommunicationBadgeCounts {
    const [counts, setCounts] = useState<CommunicationBadgeCounts>({
        unreadSms: 0,
        missedCalls: 0,
        total: 0,
    });
    const inFlightRef = useRef(false);

    const refresh = useCallback(async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const sinceMs = readCallsLastSeen();
            // Endpoints are independent — fetch in parallel and don't fail the
            // whole badge if one of them 401/503s.
            const [smsResp, callsResp] = await Promise.allSettled([
                apiClient.get<{ success: boolean; data: Record<string, number> }>('/sms/unread-counts'),
                apiClient.get<{ success: boolean; data: Call[] }>('/voice/calls'),
            ]);

            let unreadSms = 0;
            let smsError: string | null = null;
            if (smsResp.status === 'fulfilled') {
                const data = smsResp.value.data?.data || {};
                unreadSms = Object.values(data).reduce((sum, n) => sum + (Number(n) || 0), 0);
            } else {
                smsError = smsResp.reason?.message || String(smsResp.reason);
            }

            let missedCalls = 0;
            let callsError: string | null = null;
            if (callsResp.status === 'fulfilled') {
                const list = callsResp.value.data?.data || [];
                missedCalls = countMissedCalls(list, sinceMs);
            } else {
                callsError = callsResp.reason?.message || String(callsResp.reason);
            }

            const next = { unreadSms, missedCalls, total: unreadSms + missedCalls };
            setCounts(next);

            // Diagnostics: in dev, log the raw counts. In any environment,
            // expose the latest result on `window.__commBadge` so the user
            // can inspect it from the browser console while debugging.
            if (typeof window !== 'undefined') {
                (window as unknown as { __commBadge?: unknown }).__commBadge = {
                    ...next,
                    smsError,
                    callsError,
                    sinceMs,
                    fetchedAt: new Date().toISOString(),
                };
            }
            if (import.meta.env.DEV) {
                console.debug('[commBadge]', { ...next, smsError, callsError });
            }
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        refresh();
        const id = window.setInterval(refresh, POLL_INTERVAL_MS);
        const onFocus = () => refresh();
        window.addEventListener('focus', onFocus);
        return () => {
            window.clearInterval(id);
            window.removeEventListener('focus', onFocus);
        };
    }, [refresh]);

    // Live updates via the existing SMS websocket. Without these, opening a
    // conversation server-marks-it-read but the nav-bar badge would lag up
    // to 30s waiting for the next poll. We refresh on:
    //   - sms:read       → user (or another tab) cleared a conversation
    //   - sms:incoming   → a new unread arrived
    //   - sms:sent       → an admin reply went out (sometimes flips read state)
    //   - call:incoming  → an inbound call started
    //   - call:status    → terminal status; might add a missed call
    useSmsWebSocket({
        onRead: refresh,
        onIncoming: refresh,
        onSent: refresh,
        onCallIncoming: refresh,
        onCallStatus: refresh,
    });

    return counts;
}
